import { useCallback, useEffect, useReducer } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import subItemTreeReducer, {
  ancestorsOf,
  collectSubtreeIds,
  detailChildIds,
  initialTreeState,
  SubItemTreeActionType,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import {
  APPROVE_ASSET_ATTRIBUTE_CHANGE,
  APPROVE_ASSET_CHANGES,
  DELETE_ASSET,
  GET_ASSET,
  REJECT_ASSET_ATTRIBUTE_CHANGE,
  UPDATE_ASSET,
  UPDATE_ASSET_ATTRIBUTES,
} from '@atom/graph/asset';
import { GET_WORK_ORDERS_TOTAL_COUNT } from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Snackbar } from '@atom/mui';
import {
  AssetAttributeChangeInput,
  AssetAttributesUpdateInput,
  AssetDetail,
  AssetUpdateInput,
  ChangeType,
} from '@atom/types/asset';

import useChangeTypeRefresh from './subItemsTab/useChangeTypeRefresh';
import {
  InventoryDetailContextValue,
  UpdateAssetOptions,
} from './InventoryDetailContext';
import inventoryDetailReducer, {
  initialAsset,
  InventoryDetailActionType,
} from './inventoryDetailReducer';

const IN_PROGRESS_STATUS_ID = 3;

const REFRESH_ERROR_TEXT =
  'Changes were applied, but the sub items could not be refreshed.';

type InventoryAssetDetail = Omit<
  InventoryDetailContextValue,
  'conversionUnitSelection' | 'setConversionUnitSelection'
>;

const useInventoryAssetDetail = (id: string): InventoryAssetDetail => {
  const client = useApolloClient();
  const [searchParamsObject, { updateSearchParams }] = useNavigation();
  const [asset, dispatch] = useReducer(inventoryDetailReducer, initialAsset);
  const conversionUnits = asset.isMaterial
    ? (asset.budget?.conversionUnits ?? [])
    : [];
  const [subItemTree, subItemTreeDispatch] = useReducer(
    subItemTreeReducer,
    initialTreeState,
  );

  useEffect(() => {
    subItemTreeDispatch({ type: SubItemTreeActionType.RESET });
  }, [id]);

  const setAsset = (data: AssetDetail) =>
    dispatch({ type: InventoryDetailActionType.SET_ASSET, data });

  const { data, loading, error, refetch } = useQuery(GET_ASSET, {
    variables: { id, includeCursor: true },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data?.asset) {
      setAsset(data.asset);
      subItemTreeDispatch({
        type: SubItemTreeActionType.NODE_LOADED,
        data: { id: data.asset.id, detail: data.asset },
      });
    }
  }, [data]);

  const { data: workData } = useQuery(GET_WORK_ORDERS_TOTAL_COUNT, {
    variables: {
      input: {
        assetId: id,
        statusIds: [IN_PROGRESS_STATUS_ID],
        page: 1,
        limit: 1,
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (workData?.workOrders?.totalCount > 0) {
      Snackbar.info({
        message:
          'Work is in progress, any changes made could impact this work.',
      });
    }
  }, [workData]);

  const [assetUpdate] = useMutation(UPDATE_ASSET);
  const [assetAttributesUpdate] = useMutation(UPDATE_ASSET_ATTRIBUTES);
  const [assetDelete] = useMutation(DELETE_ASSET);
  const [assetChangesApprove] = useMutation(APPROVE_ASSET_CHANGES);
  const [assetAttributeChangeApprove] = useMutation(
    APPROVE_ASSET_ATTRIBUTE_CHANGE,
  );
  const [assetAttributeChangeReject] = useMutation(
    REJECT_ASSET_ATTRIBUTE_CHANGE,
  );

  const refetchAsset = async (): Promise<AssetDetail> => {
    const { data: response } = await refetch();
    return response.asset;
  };

  const fetchAssetDetail = useCallback(
    async (assetId: string): Promise<AssetDetail> => {
      const { data: response } = await client.query({
        query: GET_ASSET,
        variables: { id: assetId },
        fetchPolicy: 'no-cache',
      });
      return response.asset;
    },
    [client],
  );

  const loadNode = useCallback(
    async (assetId: string): Promise<AssetDetail | null> => {
      subItemTreeDispatch({
        type: SubItemTreeActionType.NODE_LOADING,
        data: { id: assetId },
      });

      try {
        const detail = await fetchAssetDetail(assetId);

        subItemTreeDispatch({
          type: SubItemTreeActionType.NODE_LOADED,
          data: { id: assetId, detail },
        });

        return detail;
      } catch (loadError) {
        subItemTreeDispatch({
          type: SubItemTreeActionType.NODE_ERROR,
          data: { id: assetId },
        });
        return null;
      }
    },
    [fetchAssetDetail],
  );

  const refetchNodes = useCallback(
    async (ids: string[]): Promise<AssetDetail[]> => {
      const details = await Promise.all(ids.map(loadNode));
      return details.filter(Boolean);
    },
    [loadNode],
  );

  const { refreshChangeTypes: refreshTreeChangeTypes } = useChangeTypeRefresh({
    rootAssetId: id,
    dispatch: subItemTreeDispatch,
  });

  const refreshChangeTypes = useCallback(
    async (assetIds: string[]): Promise<ChangeType | null> => {
      const rootChangeType = await refreshTreeChangeTypes(assetIds);

      dispatch({
        type: InventoryDetailActionType.UPDATE_ASSET_PROPERTY,
        data: { property: 'changeType', value: rootChangeType },
      });

      return rootChangeType;
    },
    [refreshTreeChangeTypes],
  );

  // Re-sync every loaded sub item node with the server after a change that
  // can touch pending state anywhere in the tree (approve all, move, replace).
  // Pass `rootDetail` when the root was just refetched so it isn't loaded
  // again, and `removedIds` for nodes just removed from the tree (the
  // `subItemTree` closure still has them).
  const refreshSubItemTree = async (
    rootDetail?: AssetDetail,
    removedIds: string[] = [],
  ): Promise<void> => {
    const { status } = subItemTree;
    const loadedIds = Object.keys(status).filter(
      nodeId =>
        status[nodeId] === 'loaded' &&
        (!rootDetail || nodeId !== id) &&
        !removedIds.includes(nodeId),
    );
    const details = await refetchNodes(loadedIds);

    await refreshChangeTypes(
      [...(rootDetail ? [rootDetail] : []), ...details].flatMap(detailChildIds),
    );
  };

  const updateAsset = async (
    input: AssetUpdateInput,
    options: UpdateAssetOptions = {},
  ) => {
    const { data: response } = await assetUpdate({
      variables: {
        input,
        ...(options.includeCursor && { includeCursor: true }),
      },
    });

    if (response?.assetUpdate) {
      setAsset(response.assetUpdate);
    }
  };

  const updateAttributes = async (input: AssetAttributesUpdateInput) => {
    await assetAttributesUpdate({ variables: { input } });
  };

  const deleteAsset = async () => {
    await assetDelete({ variables: { input: { id } } });
  };

  // Approving a pending delete removes that sub item (and everything under it)
  // on the server, so it can't be refetched afterwards.
  const pendingDeleteIds = (): string[] => {
    const { nodes } = subItemTree;
    const deletedIds = Object.keys(nodes).filter(
      nodeId => nodes[nodeId]?.changeType === ChangeType.DELETED,
    );

    return deletedIds.filter(
      nodeId =>
        !ancestorsOf(subItemTree, nodeId).some(ancestorId =>
          deletedIds.includes(ancestorId),
        ),
    );
  };

  const approveAllChanges = async () => {
    const { includeChangesBefore } = await fetchAssetDetail(id);
    const deletedIds = includeChangesBefore ? pendingDeleteIds() : [];
    const removedIds = R.uniq(
      deletedIds.flatMap(nodeId => collectSubtreeIds(subItemTree, nodeId)),
    );

    if (includeChangesBefore) {
      await assetChangesApprove({
        variables: {
          input: { id, rootAssetId: id, includeChangesBefore },
        },
      });
    }

    // Move a selection that was just deleted to its nearest surviving parent
    // before the nodes leave the tree, so the Sub Items tab doesn't try to
    // resolve the deleted id.
    const selectedId = (searchParamsObject.elementId as string) || null;

    if (selectedId && removedIds.includes(selectedId)) {
      const survivingParentId = ancestorsOf(subItemTree, selectedId).find(
        ancestorId => !removedIds.includes(ancestorId),
      );

      updateSearchParams({
        elementId:
          survivingParentId && survivingParentId !== id
            ? survivingParentId
            : undefined,
      });
    }

    deletedIds.forEach(nodeId =>
      subItemTreeDispatch({
        type: SubItemTreeActionType.REMOVE_NODE,
        data: { id: nodeId },
      }),
    );

    const refreshedRoot = await refetchAsset();

    try {
      await refreshSubItemTree(refreshedRoot, removedIds);
    } catch (refreshError) {
      Snackbar.error({ message: REFRESH_ERROR_TEXT });
    }
  };

  const approveAttributeChange = async (input: AssetAttributeChangeInput) => {
    await assetAttributeChangeApprove({ variables: { input } });
  };

  const rejectAttributeChange = async (input: AssetAttributeChangeInput) => {
    await assetAttributeChangeReject({ variables: { input } });
  };

  return {
    asset,
    dispatch,
    loading,
    error,
    refetch: refetchAsset,
    updateAsset,
    updateAttributes,
    deleteAsset,
    approveAllChanges,
    approveAttributeChange,
    rejectAttributeChange,
    subItemTree,
    subItemTreeDispatch,
    fetchAssetDetail,
    loadNode,
    refetchNodes,
    refreshChangeTypes,
    refreshSubItemTree,
    conversionUnits,
  };
};

export default useInventoryAssetDetail;
